import React, { createContext, useContext, useState, useCallback } from 'react';
import { agentAPI, conversationAPI, documentAPI } from '../services/api';

const AgentContext = createContext(null);

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within AgentProvider');
  }
  return context;
};

export const AgentProvider = ({ children }) => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState({
    agents: false,
    conversations: false,
    documents: false,
    query: false
  });

  // Fetch all agents
  const fetchAgents = useCallback(async () => {
    setLoading(prev => ({ ...prev, agents: true }));
    try {
      const response = await agentAPI.getAll();
      setAgents(response.data.data);
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(prev => ({ ...prev, agents: false }));
    }
  }, []);

  // Create agent
  const createAgent = async (data) => {
    const response = await agentAPI.create(data);
    const newAgent = response.data.data;
    setAgents(prev => [newAgent, ...prev]);
    return newAgent;
  };

  // Update agent
  const updateAgent = async (id, data) => {
    const response = await agentAPI.update(id, data);
    const updatedAgent = response.data.data;
    setAgents(prev => prev.map(a => a._id === id ? updatedAgent : a));
    if (selectedAgent?._id === id) {
      setSelectedAgent(updatedAgent);
    }
    return updatedAgent;
  };

  // Delete agent
  const deleteAgent = async (id) => {
    await agentAPI.delete(id);
    setAgents(prev => prev.filter(a => a._id !== id));
    if (selectedAgent?._id === id) {
      setSelectedAgent(null);
    }
  };

  // Select agent
  const selectAgent = useCallback(async (agent) => {
    setSelectedAgent(agent);
    if (agent) {
      // Fetch conversations for this agent
      setLoading(prev => ({ ...prev, conversations: true }));
      try {
        const [convRes, docRes] = await Promise.all([
          conversationAPI.getAll({ agentId: agent._id }),
          documentAPI.getAll({ agentId: agent._id })
        ]);
        setConversations(convRes.data.data);
        setDocuments(docRes.data.data);
      } catch (error) {
        console.error('Error fetching agent data:', error);
      } finally {
        setLoading(prev => ({ ...prev, conversations: false }));
      }
    } else {
      setConversations([]);
      setDocuments([]);
      setActiveConversation(null);
    }
  }, []);

  // Create conversation
  const createConversation = async (agentId) => {
    const response = await conversationAPI.create({ agentId });
    const newConv = response.data.data;
    setConversations(prev => [newConv, ...prev]);
    setActiveConversation(newConv);
    return newConv;
  };

  // Load conversation
  const loadConversation = async (id) => {
    const response = await conversationAPI.getById(id);
    setActiveConversation(response.data.data);
    return response.data.data;
  };

  // Send message
  const sendMessage = async (content, queryType = 'question') => {
    if (!activeConversation) return null;

    setLoading(prev => ({ ...prev, query: true }));
    try {
      const response = await conversationAPI.sendMessage(activeConversation._id, {
        content,
        queryType
      });

      // Update active conversation with new messages
      setActiveConversation(prev => ({
        ...prev,
        messages: [...prev.messages, ...response.data.data.messages]
      }));

      // Update conversation in list
      setConversations(prev => prev.map(c =>
        c._id === activeConversation._id
          ? { ...c, updatedAt: new Date().toISOString() }
          : c
      ));

      return response.data.data;
    } finally {
      setLoading(prev => ({ ...prev, query: false }));
    }
  };

  // Quick query (no conversation)
  const quickQuery = async (agentId, content, queryType) => {
    setLoading(prev => ({ ...prev, query: true }));
    try {
      const response = await conversationAPI.quickQuery({
        agentId,
        content,
        queryType
      });
      return response.data.data;
    } finally {
      setLoading(prev => ({ ...prev, query: false }));
    }
  };

  // Upload document
  const uploadDocument = async (file, agentId, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (agentId) formData.append('agentId', agentId);

    const response = await documentAPI.upload(formData, onProgress);
    const newDoc = response.data.data;
    setDocuments(prev => [newDoc, ...prev]);
    return newDoc;
  };

  // Delete document
  const deleteDocument = async (id) => {
    await documentAPI.delete(id);
    setDocuments(prev => prev.filter(d => d._id !== id));
  };

  // Refresh documents
  const refreshDocuments = async () => {
    if (!selectedAgent) return;
    setLoading(prev => ({ ...prev, documents: true }));
    try {
      const response = await documentAPI.getAll({ agentId: selectedAgent._id });
      setDocuments(response.data.data);
    } finally {
      setLoading(prev => ({ ...prev, documents: false }));
    }
  };

  // Export conversation
  const exportConversation = async (id) => {
    const response = await conversationAPI.export(id);
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversation-${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const value = {
    agents,
    selectedAgent,
    conversations,
    activeConversation,
    documents,
    loading,
    fetchAgents,
    createAgent,
    updateAgent,
    deleteAgent,
    selectAgent,
    createConversation,
    loadConversation,
    setActiveConversation,
    sendMessage,
    quickQuery,
    uploadDocument,
    deleteDocument,
    refreshDocuments,
    exportConversation
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

export default AgentContext;
